// web/src/pages/invoices/NewInvoicePage.tsx
//
// Single-page Xero-classic invoice creation form. Replaces the 3-step
// NewInvoiceWizard. Layout: Bill-to row → lines table → notes-left /
// totals-right footer → action row.
//
// Math: live totals via web/src/lib/invoice-totals.ts which replicates
// the server-side InvoiceComposer. Caption "Preview — confirmed on save"
// stays under the total because the server is still authoritative.
//
// ?jobId= mode: Client + lines render read-only with a "From Job: ..."
// banner; Account / Due / Comment / Delivery remain editable; Save calls
// createInvoiceFromJob.
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PageContainer } from "@/app/layouts/PageContainer";
import { ClientPicker, type ClientPickerValue } from "@/components/client-picker";
import { AccountPicker } from "@/components/account-picker";
import { IntentButton } from "@/components/intent-button";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, Plus, Trash2, X, FileText } from "lucide-react";
import {
  createInvoice,
  createInvoiceFromJob,
  type CreateInvoiceRequest,
  type CreateInvoiceLineItemRequest,
} from "@/lib/invoices-payments";
import { jobsApi } from "@/lib/jobs";
import {
  computeInvoiceTotals,
  computeLineAmount,
  validateLine,
  type LineDraft,
} from "@/lib/invoice-totals";
import { AllmarkDatePicker } from "@/components/allmark-date-picker";
import { formatDate } from "@/lib/format";

const BLANK_LINE: LineDraft = {
  description: "",
  quantity: "1",
  unitPriceExGst: "0",
  discountPct: "0",
  gstApplicable: true,
  isCredit: false,
};

function todayPlus30Iso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

function todayFormatted(): string {
  return formatDate(new Date().toISOString());
}

function toApiLine(line: LineDraft, sortOrder: number): CreateInvoiceLineItemRequest {
  return {
    description: line.description,
    quantity: Number(line.quantity),
    unitPriceExGst: Number(line.unitPriceExGst),
    discountPct: Number(line.discountPct),
    gstApplicable: line.gstApplicable,
    isCredit: line.isCredit,
    jobDetailId: null,
    sortOrder,
  };
}

export function NewInvoicePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const jobIdParam = searchParams.get("jobId");
  const jobId = jobIdParam !== null ? Number(jobIdParam) : null;

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
  }, []);

  const [client, setClient] = useState<ClientPickerValue | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [lines, setLines] = useState<LineDraft[]>([{ ...BLANK_LINE }]);
  const [dueDate, setDueDate] = useState<string>(todayPlus30Iso());
  const [comment, setComment] = useState<string>("");
  const [deliveryChargeExGst, setDeliveryChargeExGst] = useState<string>("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deliveryBlurred, setDeliveryBlurred] = useState(false);

  // Per-cell blur tracking: key is `${rowIndex}-${field}`
  const [blurred, setBlurred] = useState<Set<string>>(new Set());

  function markBlurred(rowIndex: number, field: "description" | "quantity" | "unitPriceExGst" | "discountPct") {
    setBlurred((prev) => {
      const next = new Set(prev);
      next.add(`${rowIndex}-${field}`);
      return next;
    });
  }

  function clearBlurredForRow(rowIndex: number) {
    setBlurred((prev) => {
      const next = new Set(prev);
      for (const k of prev) if (k.startsWith(`${rowIndex}-`)) next.delete(k);
      return next;
    });
  }

  function updateLine(i: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((x, j) => j === i ? { ...x, ...patch } : x));
  }

  function fieldError(line: LineDraft, field: "description" | "quantity" | "unitPriceExGst" | "discountPct"): string | null {
    if (field === "description") {
      return line.description.trim().length === 0 ? "Required" : null;
    }
    const raw = line[field];
    const n = Number(raw);
    if (!Number.isFinite(n)) return "Must be a number";
    if (field === "quantity") return n > 0 ? null : "Must be > 0";
    if (field === "unitPriceExGst") return n >= 0 ? null : "Must be ≥ 0";
    if (field === "discountPct") return (n >= 0 && n <= 100) ? null : "Must be between 0 and 100";
    return null;
  }

  const jobDetailQuery = useQuery({
    queryKey: ["job", jobId],
    queryFn: () => jobsApi.get(jobId!),
    enabled: jobId !== null,
  });
  const jobItemsQuery = useQuery({
    queryKey: ["job-items", jobId],
    queryFn: () => jobsApi.items(jobId!),
    enabled: jobId !== null,
  });
  useEffect(() => {
    if (!jobItemsQuery.data) return;
    const seeded = jobItemsQuery.data.map((item) => ({
      description: item.description,
      quantity: String(item.quantity),
      unitPriceExGst: String(item.unitPriceExGst),
      discountPct: "0",
      gstApplicable: item.gstRate > 0,
      isCredit: false,
    }));
    setLines(seeded.length > 0 ? seeded : [{ ...BLANK_LINE }]);
  }, [jobItemsQuery.data]);

  const isJobMode = jobId !== null;

  // In ?jobId= mode, supply the job's clientId to AccountPicker so it stays enabled.
  const effectiveClientId: number | null | undefined = isJobMode
    ? (jobDetailQuery.data?.clientId ?? undefined)
    : client?.clientId;

  const linesValid = useMemo(
    () => lines.length > 0 && lines.every(validateLine),
    [lines],
  );
  const deliveryError = useMemo<string | null>(() => {
    if (deliveryChargeExGst.trim() === "") return null;
    const n = Number(deliveryChargeExGst);
    if (!Number.isFinite(n)) return "Must be a number";
    if (n < 0) return "Must be ≥ 0";
    return null;
  }, [deliveryChargeExGst]);
  // In ?jobId= mode, the client comes from the job (not the form's client state),
  // so the gate is satisfied without setClient(). buildPayload() is only called in
  // the non-jobId branch.
  const saveEnabled = (isJobMode || client !== null) && linesValid && deliveryError === null;

  const createMut = useMutation({
    mutationFn: (body: CreateInvoiceRequest) => createInvoice(body),
    onSuccess: (inv) => {
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void navigate(`/invoices/${inv.invoiceId}`);
    },
    onError: (e: Error) => setSubmitError(e.message),
  });
  const createFromJobMut = useMutation({
    mutationFn: () => createInvoiceFromJob(jobId!),
    onSuccess: (inv) => {
      void qc.invalidateQueries({ queryKey: ["invoices"] });
      void navigate(`/invoices/${inv.invoiceId}`);
    },
    onError: (e: Error) => setSubmitError(e.message),
  });

  const isPending = jobId !== null ? createFromJobMut.isPending : createMut.isPending;

  function buildPayload(): CreateInvoiceRequest {
    const delivery = deliveryChargeExGst.trim() === ""
      ? null
      : Number(deliveryChargeExGst);
    return {
      clientId: client!.clientId,
      accountId,
      jobId: null,
      addressId: null,  // dropped per spec §4 / §11 — backend uses client default
      deliveryChargeExGst: delivery !== null && Number.isFinite(delivery) ? delivery : null,
      lines: lines.map((l, i) => toApiLine(l, i)),
      commentToShowOnInvoice: comment.trim() === "" ? null : comment,
      dueDate: dueDate || null,
    };
  }

  function onSave() {
    setSubmitError(null);
    if (jobId !== null) {
      createFromJobMut.mutate();
    } else {
      createMut.mutate(buildPayload());
    }
  }

  const deliveryNum = useMemo(() => {
    const n = Number(deliveryChargeExGst);
    return deliveryChargeExGst.trim() !== "" && Number.isFinite(n) ? n : 0;
  }, [deliveryChargeExGst]);

  const totals = useMemo(
    () => computeInvoiceTotals({ lines, deliveryExGst: deliveryNum }),
    [lines, deliveryNum],
  );

  // Job-mode banner data
  const jobBannerText = jobId !== null && jobDetailQuery.data
    ? `From Job: ${jobDetailQuery.data.jobReferenceNo} — line items are derived from the job and cannot be edited here.`
    : null;

  return (
    <PageContainer>
      {portalNode && createPortal(
        <button
          onClick={() => void navigate(-1)}
          className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
          type="button"
          aria-label="Back"
        >
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back
        </button>,
        portalNode
      )}

      <div className="space-y-4 pb-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">New Invoice</h1>
        </div>

        {/* Main Form Sheet */}
        <Card className="border border-slate-200 rounded-xl bg-white shadow-sm">
          <CardContent className="p-5 space-y-6">
            {/* Metadata Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div className="md:col-span-3">
                <Label htmlFor="client" className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Client</Label>
                {jobId !== null && jobDetailQuery.data ? (
                  <div id="client" className="flex h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 items-center">
                    {jobDetailQuery.data.clientName ?? "—"}
                  </div>
                ) : (
                  <ClientPicker value={client} onChange={setClient} />
                )}
              </div>
              <div className="md:col-span-1">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Issue date</Label>
                <div className="flex h-9 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 items-center cursor-not-allowed select-none" title="System sets this when the invoice is saved.">
                  {todayFormatted()}
                </div>
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="account" className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Account</Label>
                <AccountPicker
                  clientId={effectiveClientId}
                  value={accountId}
                  onChange={setAccountId}
                  placeholder={effectiveClientId != null ? "Search accounts..." : "Select a client first"}
                />
              </div>
              <div className="md:col-span-1">
                <Label htmlFor="due" className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Due date</Label>
                <AllmarkDatePicker
                  id="due"
                  value={dueDate || null}
                  onChange={(val) => setDueDate(val || "")}
                  className="h-9 border border-slate-200 bg-white hover:bg-slate-50 shadow-none w-full justify-start gap-2 rounded-md text-slate-700 text-sm font-normal"
                />
              </div>
              <div className="md:col-span-1">
                <Label htmlFor="dlv" className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Delivery charge ex GST</Label>
                <Input
                  id="dlv"
                  type="number"
                  value={deliveryChargeExGst}
                  aria-invalid={deliveryBlurred && deliveryError !== null ? true : undefined}
                  onChange={(e) => setDeliveryChargeExGst(e.target.value)}
                  onBlur={() => setDeliveryBlurred(true)}
                  className="h-9 border-slate-200 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary w-full"
                />
                {deliveryBlurred && deliveryError !== null ? (
                  <p className="mt-1 text-xs text-destructive">{deliveryError}</p>
                ) : null}
              </div>
              <div className="md:col-span-4">
                <Label htmlFor="cmt" className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Comment to show on invoice</Label>
                <Textarea
                  id="cmt"
                  className="block w-full border-slate-200 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary"
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            </div>

            {/* Job-mode banner — only renders when jobBannerText is set (job detail query has resolved). */}
            {jobBannerText !== null ? (
              <div role="status" className="rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm">
                {jobBannerText}
              </div>
            ) : null}

            <hr className="border-slate-200" />

            {/* Line Items Table */}
            <section aria-label="Line items" className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900 tracking-tight">Line Items</h2>
                {!isJobMode ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2 rounded-full border-slate-200 bg-white shadow-sm hover:bg-slate-50 font-semibold px-4"
                    onClick={() => setLines((prev) => [...prev, { ...BLANK_LINE }])}
                  >
                    <Plus className="h-4 w-4 text-slate-500" />
                    Add line
                  </Button>
                ) : null}
              </div>

              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0f3453] text-white">
                      <tr className="text-left text-xs uppercase tracking-wider">
                        <th className="px-4 py-2 font-semibold">Description</th>
                        <th className="px-4 py-2 font-semibold text-right w-24">Qty</th>
                        <th className="px-4 py-2 font-semibold text-right w-36">Unit ex GST</th>
                        <th className="px-4 py-2 font-semibold text-right w-28">Disc %</th>
                        <th className="px-4 py-2 font-semibold text-center w-20">GST</th>
                        <th className="px-4 py-2 font-semibold text-center w-20">Credit</th>
                        <th className="px-4 py-2 font-semibold text-right w-32">Amount</th>
                        <th className="px-4 py-2 w-12"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {lines.map((l, i) => (
                        <tr key={i} className="hover:bg-slate-50/30 transition-colors">
                          {isJobMode ? (
                            <>
                              <td className="px-4 py-3 text-slate-700">{l.description}</td>
                              <td className="px-4 py-3 text-right text-slate-700">{l.quantity}</td>
                              <td className="px-4 py-3 text-right text-slate-700">{l.unitPriceExGst}</td>
                              <td className="px-4 py-3 text-right text-slate-700">{l.discountPct}</td>
                              <td className="px-4 py-3 text-center text-slate-700">{l.gstApplicable ? "✓" : "—"}</td>
                              <td className="px-4 py-3 text-center text-slate-700">{l.isCredit ? "✓" : "—"}</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900 tabular-nums">
                                {(() => {
                                  const amt = computeLineAmount(l);
                                  return amt === null ? "" : amt.toFixed(2);
                                })()}
                              </td>
                              <td className="px-4 py-3"></td>
                            </>
                          ) : (
                            <>
                              <td className="p-1 px-2">
                                <Input
                                  aria-label={`Description ${i + 1}`}
                                  value={l.description}
                                  aria-invalid={blurred.has(`${i}-description`) && fieldError(l, "description") !== null ? true : undefined}
                                  onChange={(e) => updateLine(i, { description: e.target.value })}
                                  onBlur={() => markBlurred(i, "description")}
                                  placeholder={blurred.has(`${i}-description`) && fieldError(l, "description") !== null ? (fieldError(l, "description") ?? "Required") : "Enter description..."}
                                  className={cn(
                                    "border transition-all h-9 bg-white",
                                    blurred.has(`${i}-description`) && fieldError(l, "description") !== null
                                      ? "border-destructive text-destructive placeholder:text-destructive focus:border-destructive focus-visible:ring-destructive/20 font-medium"
                                      : "border-slate-200 focus:border-slate-300 focus-visible:ring-primary/20"
                                  )}
                                />
                                {blurred.has(`${i}-description`) && fieldError(l, "description") !== null ? (
                                  <p className="sr-only">{fieldError(l, "description")}</p>
                                ) : null}
                              </td>
                              <td className="p-1">
                                <Input
                                  aria-label={`Quantity ${i + 1}`}
                                  type="number"
                                  value={l.quantity}
                                  aria-invalid={blurred.has(`${i}-quantity`) && fieldError(l, "quantity") !== null ? true : undefined}
                                  onChange={(e) => updateLine(i, { quantity: e.target.value })}
                                  onBlur={() => markBlurred(i, "quantity")}
                                  placeholder={blurred.has(`${i}-quantity`) && fieldError(l, "quantity") !== null ? (fieldError(l, "quantity") ?? "Must be > 0") : "1"}
                                  className={cn(
                                    "text-right border transition-all h-9 bg-white",
                                    blurred.has(`${i}-quantity`) && fieldError(l, "quantity") !== null
                                      ? "border-destructive text-destructive placeholder:text-destructive focus:border-destructive focus-visible:ring-destructive/20 font-medium"
                                      : "border-slate-200 focus:border-slate-300 focus-visible:ring-primary/20"
                                  )}
                                />
                                {blurred.has(`${i}-quantity`) && fieldError(l, "quantity") !== null ? (
                                  <p className="sr-only">{fieldError(l, "quantity")}</p>
                                ) : null}
                              </td>
                              <td className="p-1">
                                <Input
                                  aria-label={`Unit price ${i + 1}`}
                                  type="number"
                                  value={l.unitPriceExGst}
                                  aria-invalid={blurred.has(`${i}-unitPriceExGst`) && fieldError(l, "unitPriceExGst") !== null ? true : undefined}
                                  onChange={(e) => updateLine(i, { unitPriceExGst: e.target.value })}
                                  onBlur={() => markBlurred(i, "unitPriceExGst")}
                                  placeholder={blurred.has(`${i}-unitPriceExGst`) && fieldError(l, "unitPriceExGst") !== null ? (fieldError(l, "unitPriceExGst") ?? "Must be >= 0") : "0"}
                                  className={cn(
                                    "text-right border transition-all h-9 bg-white",
                                    blurred.has(`${i}-unitPriceExGst`) && fieldError(l, "unitPriceExGst") !== null
                                      ? "border-destructive text-destructive placeholder:text-destructive focus:border-destructive focus-visible:ring-destructive/20 font-medium"
                                      : "border-slate-200 focus:border-slate-300 focus-visible:ring-primary/20"
                                  )}
                                />
                                {blurred.has(`${i}-unitPriceExGst`) && fieldError(l, "unitPriceExGst") !== null ? (
                                  <p className="sr-only">{fieldError(l, "unitPriceExGst")}</p>
                                ) : null}
                              </td>
                              <td className="p-1">
                                <Input
                                  aria-label={`Discount % ${i + 1}`}
                                  type="number"
                                  value={l.discountPct}
                                  aria-invalid={blurred.has(`${i}-discountPct`) && fieldError(l, "discountPct") !== null ? true : undefined}
                                  onChange={(e) => updateLine(i, { discountPct: e.target.value })}
                                  onBlur={() => markBlurred(i, "discountPct")}
                                  placeholder={blurred.has(`${i}-discountPct`) && fieldError(l, "discountPct") !== null ? (fieldError(l, "discountPct") ?? "Must be between 0 and 100") : "0"}
                                  className={cn(
                                    "text-right border transition-all h-9 bg-white",
                                    blurred.has(`${i}-discountPct`) && fieldError(l, "discountPct") !== null
                                      ? "border-destructive text-destructive placeholder:text-destructive focus:border-destructive focus-visible:ring-destructive/20 font-medium"
                                      : "border-slate-200 focus:border-slate-300 focus-visible:ring-primary/20"
                                  )}
                                />
                                {blurred.has(`${i}-discountPct`) && fieldError(l, "discountPct") !== null ? (
                                  <p className="sr-only">{fieldError(l, "discountPct")}</p>
                                ) : null}
                              </td>
                              <td className="p-1 text-center">
                                <input
                                  aria-label={`GST applicable ${i + 1}`}
                                  type="checkbox"
                                  checked={l.gstApplicable}
                                  onChange={(e) => updateLine(i, { gstApplicable: e.target.checked })}
                                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary transition-all cursor-pointer"
                                />
                              </td>
                              <td className="p-1 text-center">
                                <input
                                  aria-label={`Is credit ${i + 1}`}
                                  type="checkbox"
                                  checked={l.isCredit}
                                  onChange={(e) => updateLine(i, { isCredit: e.target.checked })}
                                  className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary transition-all cursor-pointer"
                                />
                              </td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900 tabular-nums">
                                {(() => {
                                  const amt = computeLineAmount(l);
                                  return amt === null ? "" : amt.toFixed(2);
                                })()}
                              </td>
                              <td className="p-1 text-center">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all"
                                  aria-label={`Remove ${i + 1}`}
                                  disabled={lines.length === 1}
                                  onClick={() => {
                                    clearBlurredForRow(i);
                                    setLines((prev) => prev.filter((_, j) => j !== i));
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50/50 border-t border-slate-200">
                      <tr>
                        <td colSpan={6} className="px-4 py-2.5 text-right font-medium text-slate-500">Subtotal</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-900 tabular-nums">{totals.subtotal.toFixed(2)}</td>
                        <td></td>
                      </tr>
                      {deliveryNum !== 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-2.5 text-right font-medium text-slate-500 text-xs">Delivery</td>
                          <td className="px-4 py-2.5 text-right font-semibold text-slate-900 tabular-nums text-xs">+{deliveryNum.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      ) : null}
                      <tr>
                        <td colSpan={6} className="px-4 py-2.5 text-right font-medium text-slate-500">GST</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-slate-900 tabular-nums">{totals.gst.toFixed(2)}</td>
                        <td></td>
                      </tr>
                      <tr className="bg-slate-100/30">
                        <td colSpan={6} className="px-4 py-2.5 text-right font-bold text-slate-900 text-base">Total</td>
                        <td className="px-4 py-2.5 text-right font-bold text-slate-900 text-base tabular-nums">{totals.total.toFixed(2)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
              <p className="text-center text-xs text-slate-400 italic">Preview — confirmed on save.</p>
            </section>
          </CardContent>
        </Card>

        {submitError !== null ? (
          <p className="text-sm text-destructive" role="alert">{submitError}</p>
        ) : null}

        <div className="flex justify-between items-center gap-4 mt-6">
          <IntentButton
            intent="ghost"
            onClick={() => void navigate(-1)}
            className="border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-semibold shadow-sm h-9 px-5 rounded-md flex items-center gap-2"
          >
            <X className="h-4 w-4 text-slate-500" />
            Cancel
          </IntentButton>
          <IntentButton
            onClick={onSave}
            disabled={!saveEnabled}
            loading={isPending}
            className="px-5 rounded-md bg-[#0f3453] text-white hover:bg-[#152e46] hover:shadow-md disabled:bg-slate-100 disabled:text-slate-400 disabled:border disabled:border-slate-200 disabled:shadow-none transition-all duration-150 h-9 font-semibold shadow-sm flex items-center gap-2"
          >
            <FileText className="h-4 w-4" />
            Save as draft
          </IntentButton>
        </div>
      </div>
    </PageContainer>
  );
}
